/**
 * @file use-localized-continents.ts
 *
 * Reads the shipping store's region tree and returns it with continent + country
 * names localized to the active admin locale (see {@link localizeContinents}).
 * Memoized on the tree + locale so a runtime language switch re-localizes without a
 * refetch, and the shared display helpers (region labels, the "ships to" summary,
 * the country `<select>` options) all read the localized names for free.
 */

import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { ShopContinent } from '../api/shop-types';
import { useShippingStore } from '../stores/shipping.store';
import { localizeContinents } from './region-localization';

/**
 * Returns the store's continent tree with names localized to the active locale.
 *
 * @returns The localized {@link ShopContinent} tree.
 */
export function useLocalizedContinents(): ShopContinent[] {
  const continents = useShippingStore((s) => s.continents);
  const { locale } = useIntl();
  return useMemo(() => localizeContinents(continents, locale), [continents, locale]);
}
